import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCampaignPermissions } from '../../../hooks/useCampaignPermissions';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import CsvUploadZone from '../../../components/CallcenterV2/CsvUploadZone';
import { addDnc, fetchDnc, importDnc, removeDnc } from '../../../services/callcenterV2/campaignsService';
import {
  MAX_DNC_PHONES,
  chunkPhones,
  downloadDncTemplate,
  extractDncPhones,
} from '../../../services/callcenterV2/csvUtils';
import '../../../components/CallcenterV2/ccv2-components.css';
import '../config-styles.css';

const REASON_LABELS = {
  opt_out: 'Opt-out do contato',
  manual: 'Bloqueio manual',
  regulatory: 'Regulatório (Não Me Perturbe)',
  import: 'Importação',
};

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 50;

const readFileText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Não foi possível ler o arquivo.'));
    reader.readAsText(file, 'utf-8');
  });

function Dnc() {
  const { canManage } = useCampaignPermissions();

  const [entries, setEntries] = useState([]);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [form, setForm] = useState({ phone: '', reason: 'manual', notes: '' });
  const [file, setFile] = useState(null);
  const [bulk, setBulk] = useState('');
  const [importing, setImporting] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [pendingRemove, setPendingRemove] = useState(null);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setAppliedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => { setOffset(0); }, [appliedSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset };
      if (appliedSearch) params.phone = appliedSearch;
      const data = await fetchDnc(params);
      const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      setEntries(items);
      setTotal(Number.isFinite(Number(data?.total)) ? Number(data.total) : items.length);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, offset]);

  useEffect(() => { load(); }, [load]);

  const notify = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 4000);
  };

  const add = async () => {
    setSaving(true);
    try {
      await addDnc({
        phone: form.phone.trim(),
        reason: form.reason,
        notes: form.notes.trim() || undefined,
      });
      setForm({ phone: '', reason: 'manual', notes: '' });
      notify('Número bloqueado.');
      setError(null);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const runImport = async () => {
    setError(null);
    setImportResult(null);
    setImporting({ done: 0, total: 0 });

    let parsed;
    try {
      const text = file ? await readFileText(file) : bulk;
      parsed = extractDncPhones(text);
    } catch (e) {
      setError(e.message);
      setImporting(null);
      return;
    }

    if (parsed.phones.length === 0) {
      setError('Nenhum telefone válido encontrado. Use um número por linha, com DDD.');
      setImporting(null);
      return;
    }

    const chunks = chunkPhones(parsed.phones);
    let inserted = 0;
    let skipped = 0;
    let invalid = parsed.invalidCount;
    let processed = 0;

    setImporting({ done: 0, total: parsed.phones.length });

    try {
      for (const chunk of chunks) {
        const result = await importDnc(chunk, 'import');
        inserted += result.inserted ?? 0;
        skipped += result.skipped ?? 0;
        invalid += result.invalid ?? 0;
        processed += chunk.length;
        setImporting({ done: processed, total: parsed.phones.length });
      }
      setImportResult({
        inserted,
        skipped,
        invalid,
        truncated: parsed.truncated,
        samples: parsed.invalid,
      });
      setFile(null);
      setBulk('');
      notify(`Importados ${inserted} números (${skipped} já existiam, ${invalid} inválidos).`);
      load();
    } catch (e) {
      setError(
        `Falha após ${processed} de ${parsed.phones.length} números: ${e.message}. ` +
        'Reenvie o arquivo — os já bloqueados são ignorados automaticamente.',
      );
    } finally {
      setImporting(null);
    }
  };

  const runRemove = async () => {
    if (!pendingRemove) return;
    setSaving(true);
    try {
      await removeDnc(pendingRemove.phoneE164 || pendingRemove.phone);
      setPendingRemove(null);
      notify('Número liberado.');
      setError(null);
      load();
    } catch (e) {
      setError(e.message);
      setPendingRemove(null);
    } finally {
      setSaving(false);
    }
  };

  const canImport = canManage && !importing && (Boolean(file) || bulk.trim().length > 0);

  return (
    <div className="ccv2-page-container ccv2-p5-container">
      <div className="ccv2-page-header">
        <div className="ccv2-page-title-area">
          <h1>Lista de bloqueio (DNC)</h1>
          <p>Números que o discador nunca chama — checado no import, no hopper e antes de cada originate</p>
        </div>
        <div className="ccv2-page-actions">
          <Link className="ccv2-btn ccv2-btn-secondary" to="/callcenterV2/campaigns">← Campanhas</Link>
          <button type="button" className="ccv2-btn ccv2-btn-secondary" onClick={load}>🔄 Atualizar</button>
        </div>
      </div>

      {success && <div className="ccv2-p5-success">{success}</div>}
      {error && (
        <div className="ccv2-p5-error">
          ⚠️ {error}
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        </div>
      )}

      {canManage && (
        <div className="ccv2-config-card ccv2-card-gap">
          <div className="ccv2-card-title">
            <div>
              <h3>Bloquear número</h3>
              <p>Aceita o formato nacional (11999998888) ou internacional (+5511999998888).</p>
            </div>
          </div>
          <div className="ccv2-card-body">
            <div className="ccv2-form-row">
              <div className="ccv2-form-group">
                <label htmlFor="ccv2-dnc-phone">Telefone</label>
                <input
                  id="ccv2-dnc-phone"
                  value={form.phone}
                  onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                  placeholder="11999998888 ou +5511999998888"
                />
              </div>
              <div className="ccv2-form-group">
                <label htmlFor="ccv2-dnc-reason">Motivo</label>
                <select
                  id="ccv2-dnc-reason"
                  value={form.reason}
                  onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
                >
                  {Object.entries(REASON_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="ccv2-form-group">
              <label htmlFor="ccv2-dnc-notes">Observação</label>
              <input
                id="ccv2-dnc-notes"
                value={form.notes}
                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                maxLength={160}
                placeholder="opcional — fica registrado na auditoria"
              />
            </div>

            <button
              type="button"
              className="ccv2-btn ccv2-btn-primary"
              disabled={!form.phone.trim() || saving}
              onClick={add}
            >
              Bloquear número
            </button>
          </div>
        </div>
      )}

      {canManage && (
        <div className="ccv2-config-card ccv2-card-gap">
          <div className="ccv2-card-title">
            <div>
              <h3>Importar lista de bloqueio</h3>
              <p>Arquivo CSV/TXT ou colagem direta. Números repetidos e já bloqueados são ignorados.</p>
            </div>
            <button type="button" className="ccv2-btn ccv2-btn-outline ccv2-btn-sm" onClick={downloadDncTemplate}>
              ⬇ Baixar modelo
            </button>
          </div>
          <div className="ccv2-card-body">
            <div className="ccv2-form-group">
              <label>Arquivo</label>
              <CsvUploadZone
                accept=".csv,.txt,text/csv,text/plain"
                file={file}
                onFile={setFile}
                disabled={Boolean(importing) || bulk.trim().length > 0}
                hint={`CSV ou TXT · até ${MAX_DNC_PHONES.toLocaleString('pt-BR')} números por importação`}
              />
            </div>

            <div className="ccv2-form-group">
              <label htmlFor="ccv2-dnc-bulk">Ou cole os números (um por linha)</label>
              <textarea
                id="ccv2-dnc-bulk"
                rows={4}
                value={bulk}
                onChange={(e) => setBulk(e.target.value)}
                disabled={Boolean(file) || Boolean(importing)}
                placeholder={'11999998888\n1133334444'}
              />
              <p className="ccv2-hint">
                A coluna <code>telefone</code> é detectada automaticamente; sem cabeçalho, usamos a primeira coluna.
                O envio é feito em lotes de 5.000 e pode ser repetido com segurança.
              </p>
            </div>

            <button type="button" className="ccv2-btn ccv2-btn-primary" onClick={runImport} disabled={!canImport}>
              {importing
                ? `Importando ${importing.done.toLocaleString('pt-BR')}/${importing.total.toLocaleString('pt-BR')}...`
                : '⬆ Importar lista'}
            </button>

            {importResult && (
              <div className="ccv2-stack-top">
                <div className="ccv2-stat-tiles">
                  <div className="ccv2-stat-tile ccv2-stat-tile--success">
                    <span className="ccv2-stat-tile-label">Bloqueados</span>
                    <span className="ccv2-stat-tile-value">{importResult.inserted}</span>
                  </div>
                  <div className="ccv2-stat-tile">
                    <span className="ccv2-stat-tile-label">Já existiam</span>
                    <span className="ccv2-stat-tile-value">{importResult.skipped}</span>
                  </div>
                  <div className={`ccv2-stat-tile${importResult.invalid > 0 ? ' ccv2-stat-tile--danger' : ''}`}>
                    <span className="ccv2-stat-tile-label">Inválidos</span>
                    <span className="ccv2-stat-tile-value">{importResult.invalid}</span>
                  </div>
                </div>

                {importResult.truncated && (
                  <div className="ccv2-p5-error ccv2-stack-top">
                    Lista truncada em {MAX_DNC_PHONES.toLocaleString('pt-BR')} números. Envie o restante em outro arquivo.
                  </div>
                )}

                {importResult.samples.length > 0 && (
                  <div className="ccv2-stack-top">
                    <p className="ccv2-hint">Amostra dos valores recusados na leitura:</p>
                    <div className="ccv2-config-table-wrapper">
                      <table className="ccv2-config-table">
                        <thead><tr><th>Linha</th><th>Valor</th></tr></thead>
                        <tbody>
                          {importResult.samples.map((sample, index) => (
                            <tr key={`${sample.line}-${index}`}>
                              <td>{sample.line}</td>
                              <td className="ccv2-cell-phone">{sample.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="ccv2-filters">
        <div className="ccv2-filter-group">
          <label htmlFor="ccv2-dnc-search">Buscar telefone</label>
          <input
            id="ccv2-dnc-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="11999998888"
          />
        </div>
      </div>

      {loading ? (
        <div className="ccv2-config-loading"><div className="ccv2-spinner" /></div>
      ) : (
        <div className="ccv2-config-card">
          <div className="ccv2-config-table-wrapper">
            <table className="ccv2-config-table">
              <thead>
                <tr>
                  <th>Telefone</th>
                  <th>Motivo</th>
                  <th>Observação</th>
                  <th>Bloqueado em</th>
                  <th>Expira</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 ? (
                  <tr><td colSpan={6} className="ccv2-empty-row">Nenhum número bloqueado.</td></tr>
                ) : entries.map((entry) => {
                  const phone = entry.phoneE164 || entry.phone;
                  return (
                    <tr key={phone}>
                      <td className="ccv2-cell-phone">{phone}</td>
                      <td>{REASON_LABELS[entry.reason] || entry.reason}</td>
                      <td>{entry.notes || '—'}</td>
                      <td className="ccv2-cell-datetime">
                        {entry.createdAt ? new Date(entry.createdAt).toLocaleString('pt-BR') : '—'}
                      </td>
                      <td className="ccv2-cell-datetime">
                        {entry.expiresAt ? new Date(entry.expiresAt).toLocaleString('pt-BR') : 'nunca'}
                      </td>
                      <td>
                        {canManage && (
                          <div className="ccv2-cell-actions">
                            <button
                              type="button"
                              className="ccv2-btn-icon ccv2-btn-delete"
                              title="Liberar número"
                              onClick={() => setPendingRemove(entry)}
                            >
                              🗑️
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="ccv2-inline-meta">
            <span>
              {total === 0
                ? 'Nenhum número bloqueado'
                : `Mostrando ${offset + 1}–${Math.min(offset + entries.length, total)} de ${total}`}
            </span>
            <button
              type="button"
              className="ccv2-btn ccv2-btn-outline ccv2-btn-sm"
              disabled={loading || offset === 0}
              onClick={() => setOffset((prev) => Math.max(0, prev - PAGE_SIZE))}
            >
              ← Anteriores
            </button>
            <button
              type="button"
              className="ccv2-btn ccv2-btn-outline ccv2-btn-sm"
              disabled={loading || offset + entries.length >= total}
              onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
            >
              Próximos →
            </button>
          </div>
        </div>
      )}

      <ConfirmModal
        open={Boolean(pendingRemove)}
        title={pendingRemove ? `Liberar ${pendingRemove.phoneE164 || pendingRemove.phone}?` : ''}
        body="O número volta a ser discável pelas campanhas. A remoção fica registrada na auditoria."
        confirmLabel="Liberar"
        danger
        loading={saving}
        onCancel={() => setPendingRemove(null)}
        onConfirm={runRemove}
      />
    </div>
  );
}

export default Dnc;
